// eslint-disable-next-line no-unused-vars
import { Select } from "antd";
import { css } from "glamor";
import * as R from "ramda";
import React, { Component } from "react";

const styles = {
    taggingToolbar: css({
        display: "flex",
    }),
    taggingToolbarSelect: css({
        "& li": {
            whiteSpace: "normal",
        },
    }),
    taggingSelect: {
        width: "200px",
    },
    taggingToolbarContainer: css({
        display: "flex",
        alignItems: "center",
    }),
    taggingDeleteButton: css({
        marginRight: 15,
    }),
};

const renameKeys = R.curry((keysMap, obj) =>
    R.reduce(
        (acc, key) => R.assoc(keysMap[key] || key, obj[key], acc),
        {},
        R.keys(obj)
    )
);

const isFirstLevelTag = obj => !R.has("condition", obj);

const hasAnyOfSelectedTags = selectedTags => tagGroup => {
    const parentTags = R.pathOr([], ["condition", "tagsAny"], tagGroup);
    return !R.isEmpty(R.intersection(parentTags, selectedTags));
};

export const extractTagsFromConfig = taggingConfig => {
    const tags = R.pipe(R.pluck("tags"), R.flatten)(taggingConfig.groups);
    const transformTags = R.pipe(
        R.map(renameKeys({ tag: "value", label: "label" })),
        R.indexBy(R.prop("value"))
    );
    return transformTags(tags);
};

class TaggingToolbar extends Component {
    constructor(props) {
        super(props);
        const { selectedTags, taggingConfig } = this.props;

        this.tagOptions = extractTagsFromConfig(taggingConfig);
        this.props.clearTagsRef(this.clearTags);

        this.state = {
            selectedTags: selectedTags || [],
        };

        this.popupContainer = null;
    }

    findTagGroupByTagId(tagId) {
        const { taggingConfig } = this.props;
        const tagIdInGroup = group => {
            return R.contains(tagId, R.pluck("tag", group.tags));
        };
        return R.find(tagIdInGroup)(taggingConfig.groups);
    }

    handleChange = (tagGroup, isMultiple) => newTag => {
        const filterIfNotInCurrentGroup = R.reject(
            R.contains(R.__, R.map(R.prop("tag"), tagGroup.tags))
        );
        const isTagVisible = tagsToSave => tag => {
            if (tag === undefined) {
                return false;
            }
            const tagGroup = this.findTagGroupByTagId(tag);
            return this.isTagGroupVisible(tagsToSave)(tagGroup);
        };
        let tagsToSave;

        if (!isMultiple) {
            const newTags = [
                ...new Set([
                    ...filterIfNotInCurrentGroup(this.state.selectedTags),
                    newTag,
                ]),
            ];
            tagsToSave = R.filter(isTagVisible(newTags), newTags);
        } else {
            tagsToSave = [this.state.selectedTags[0], ...newTag];
        }

        if (R.isEmpty(this.state.selectedTags) && this.props.onNew) {
            this.props.onNew(tagsToSave);
        } else if (this.props.onUpdate) {
            this.props.onUpdate(tagsToSave);
        }

        // Optimistic update
        this.setState({ selectedTags: tagsToSave });
    };

    isTagGroupVisible = selectedTags => tagGroup => {
        return R.either(
            isFirstLevelTag,
            hasAnyOfSelectedTags(selectedTags)
        )(tagGroup);
    };

    clearTags = () => {
        this.setState({ selectedTags: [] }, () => {
            this.props.onUpdate([]);
        });
    };

    getOptionsForTagGroup(tagGroup) {
        const convertTagIDtoOption = tag => {
            return this.tagOptions[tag.tag];
        };
        return R.map(convertTagIDtoOption, tagGroup.tags);
    }

    getSelectedForTagGroup(tagGroup) {
        const getFirstSelectedTag = R.pipe(
            R.map(R.prop("tag")),
            R.intersection(this.state.selectedTags),
            R.head
        );
        return getFirstSelectedTag(tagGroup.tags);
    }

    getTagGroupByParentTag(parentTag) {
        return this.props.taggingConfig.groups.find(
            hasAnyOfSelectedTags([parentTag])
        );
    }

    getMainTagGroup() {
        return this.props.taggingConfig.groups.find(isFirstLevelTag);
    }

    renderSelect(tagGroup, isMultiple) {
        if (!tagGroup) return null;

        const Wrapper = this.props.wrapper;
        const selectedTags = this.state.selectedTags.slice(1);
        const dataTestId = `Disposition-Select-${tagGroup.displayName.replace(
            /\s+/g,
            "-"
        )}`;
        return (
            <Wrapper label={tagGroup.displayName}>
                <div ref={e => (this.popupContainer = e)} />

                <Select
                    data-test-id={dataTestId}
                    dropdownClassName={styles.taggingToolbarSelect.toString()}
                    onChange={this.handleChange(tagGroup, isMultiple)}
                    placeholder={tagGroup.displayName}
                    value={
                        isMultiple
                            ? selectedTags
                            : this.getSelectedForTagGroup(tagGroup)
                    }
                    allowClear
                    mode={isMultiple && "multiple"}
                    style={styles.taggingSelect}
                    getPopupContainer={() => this.popupContainer}
                >
                    {this.getOptionsForTagGroup(tagGroup).map(option => (
                        <Select.Option key={option.value} value={option.value}>
                            {option.label}
                        </Select.Option>
                    ))}
                </Select>
            </Wrapper>
        );
    }

    render() {
        const mainTag = this.state.selectedTags[0];
        const childrenTagGroup = this.getTagGroupByParentTag(mainTag);
        const mainTagGroup = this.getMainTagGroup();
        // AR-11281 removed launch darkly "multiple-dispositions"
        const isMultiple = false;

        return (
            <div
                className="arachnys-tagging-toolbar"
                {...styles.taggingToolbar}
            >
                {this.renderSelect(mainTagGroup)}
                {this.renderSelect(childrenTagGroup, isMultiple)}
            </div>
        );
    }
}

export default TaggingToolbar;
